// One environment of the Environments tab (spec/dashboard.md section 12.2; R-BR-04; R-ACC-01 protected environments): what
// it runs in words, the active pins, Pin… / Revoke / Rollback… / Promote to <next>…, the pin history, the pointer the
// clients read and the routing simulator. `deploy` on the environment gates every write (on the target for Promote…);
// every write re-reads the pins and the pointer (of the target too). `autoOpen` (the `?pin=<env>&release=<id>` deep link)
// opens a dialog.
import { useEffect, useState } from 'react';
import { ops } from '../../api/operations';
import type { Environment, Release, ReleasePin, ResolvedRelease } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { useConfirm } from '../../ui/ConfirmDialog';
import { TextInput } from '../../ui/inputs';
import { formatInstant, useTimeZone } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { queryCache, useQuery } from '../../workspace/cache';
import { PinDialog, RollbackDialog, useActivePins, usePinHistory } from './PinDialogs';
import { PromoteDialog } from './PromoteDialog';
import { agoText, asApiError, currentRelease, keys, nextEnvironment, pinState, pretty, rangeText, releaseName } from './releases';
import { LoadError, SkeletonRows } from '../../ui/PageStates';

/** The environment's game clock in words: prod runs on UTC, the others may be shifted or follow the device. */
function gameTime(e: Environment): string {
  if (e.kind === 'prod') return 'UTC';
  if (e.useLocalTime) return 'device local time';
  const offset = Number(e.gameTimeOffsetSeconds);
  return offset ? `UTC ${offset > 0 ? '+' : ''}${offset / 3600} h` : 'UTC';
}
import { ArrowRight, ChevronRight, History, Pin, RotateCcw } from '../../ui/icons';

/** The active pins of an environment (shared with the Releases tab's "pinned on" column and the dialogs). */
export function usePins(env: string) {
  return useActivePins(env);
}

export type EnvironmentDialog = 'pin' | 'promote' | 'rollback';

export function EnvironmentCard({ env, releases, autoOpen }: { env: Environment; releases: Release[]; autoOpen?: { dialog: EnvironmentDialog; releaseId?: string } | null }) {
  const { org, game, can, environments } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const confirm = useConfirm();
  const [zone] = useTimeZone();
  const { offsets } = useGame();
  const missing = can('deploy', env.slug) ? null : 'deploy';
  const pinsQ = usePins(env.slug);
  const [showHistory, setShowHistory] = useState(false);
  const [dialog, setDialog] = useState<EnvironmentDialog | null>(null);
  const [initialReleaseId, setInitialReleaseId] = useState<string | undefined>(undefined);
  const [revoking, setRevoking] = useState<string | null>(null);
  const path = { org, game: game.slug, env: env.slug };
  const next = nextEnvironment(environments, env);

  // the deep link: open the asked dialog whenever `autoOpen` changes to a value
  const autoDialog = autoOpen?.dialog ?? null;
  const autoRelease = autoOpen?.releaseId;
  useEffect(() => {
    if (!autoDialog) return;
    if (autoDialog === 'promote' && !next) return;
    setInitialReleaseId(autoDialog === 'pin' ? autoRelease : undefined);
    setDialog(autoDialog);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reopen only on a new deep link, not when `next` resolves
  }, [autoDialog, autoRelease]);

  const refreshEnv = (slug: string) => {
    queryCache.invalidate(keys.pins(org, game.slug, slug)); // the history's key is under it
    queryCache.invalidate(keys.pointer(org, game.slug, slug));
  };
  const refresh = () => refreshEnv(env.slug);
  const closeDialog = () => {
    setDialog(null);
    setInitialReleaseId(undefined);
  };
  const when = (v: string | null | undefined) => formatInstant(v, zone, { offsets });

  const active = (pinsQ.data?.items ?? []).filter((p) => !p.revokedAt && !p.supersededById);
  const defaultPin = active.find((p) => p.isDefault);
  const ranges = active.filter((p) => !p.isDefault);
  const runs = currentRelease(pinsQ.data?.items, releases);
  const promoteMissing = next && !can('deploy', next.slug) ? 'deploy' : null;

  const revoke = async (pin: ReleasePin) => {
    const what = pin.isDefault ? `the default pin (${releaseName(pin.releaseId, releases)})` : `the range pin ${rangeText(pin)} (${releaseName(pin.releaseId, releases)})`;
    const ok = await confirm({ title: 'Revoke the pin?', body: `Revoke ${what} on ${env.name}? Clients stop being routed to it within the pointer's TTL.`, confirmLabel: 'Revoke', tone: 'danger' });
    if (!ok) return;
    setRevoking(pin.id);
    try {
      await client.data(ops.revokePin, { path: { ...path, pin: pin.id } });
      toasts.success('Pin revoked');
      refresh();
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setRevoking(null);
    }
  };

  const pinLine = (pin: ReleasePin) => (
    <li key={pin.id} className="pin-line" data-testid={`pin-${pin.id}`}>
      <span className="pin-what">
        {pin.isDefault ? <Badge tone="accent">default</Badge> : <Badge tone="info">{rangeText(pin)}</Badge>} <strong>{releaseName(pin.releaseId, releases)}</strong>
        {releaseName(pin.releaseId, releases) !== pin.releaseId && <code className="small muted"> {pin.releaseId}</code>}
      </span>
      <span className="muted small">
        {pin.pinnedBy?.label ?? '—'} · {when(pin.createdAt)}
        {pin.reason ? ` · ${pin.reason}` : ''}
      </span>
      <Button size="s" variant="ghost" onClick={() => void revoke(pin)} busy={revoking === pin.id} missingPermission={missing} title={pin.isDefault ? 'Revoke the default pin' : `Revoke the range ${rangeText(pin)}`}>
        Revoke
      </Button>
    </li>
  );

  const err = pinsQ.error ? asApiError(pinsQ.error) : null;
  return (
    <article className="pane env-card" data-testid={`env-card-${env.slug}`} aria-label={env.name}>
      <header className="pane-head env-card-head">
        <h2>{env.name}</h2>
        <code>{env.slug}</code>
        <Badge tone={env.kind === 'prod' ? 'danger' : env.kind === 'stage' ? 'warning' : 'info'}>{env.kind}</Badge>
        {env.isProtected && (
          <Badge tone="danger" title="Pins and rollbacks here follow the protected-environment rules">
            protected
          </Badge>
        )}
        {pinsQ.data && (
          <span className="env-runs muted small" data-testid={`env-runs-${env.slug}`}>
            {defaultPin ? `runs ${releaseName(defaultPin.releaseId, releases)}${runs ? ` · ${runs.branchName}` : ''} · ${agoText(defaultPin.createdAt)}` : 'nothing pinned'}
          </span>
        )}
        <span className="spacer" />
        {next && (
          <Button
            size="s"
            variant="ghost"
            icon={<ArrowRight aria-hidden="true" />}
            onClick={() => setDialog('promote')}
            disabled={!defaultPin}
            missingPermission={promoteMissing}
            title={!defaultPin ? `Nothing is pinned on ${env.name} to promote` : `Pin what ${env.name} runs on ${next.name}`}
          >
            Promote to {next.name}…
          </Button>
        )}
        <Button size="s" icon={<RotateCcw aria-hidden="true" />} onClick={() => setDialog('rollback')} missingPermission={missing}>
          Rollback…
        </Button>
        <Button
          size="s"
          variant="primary"
          icon={<Pin aria-hidden="true" />}
          onClick={() => {
            setInitialReleaseId(undefined);
            setDialog('pin');
          }}
          missingPermission={missing}
        >
          Pin…
        </Button>
      </header>
      <div className="pane-body">
      <div className="facts env-card-facts">
        <span className="label-value">
          <span className="label">Game time</span>
          {gameTime(env)}
        </span>
        {env.isProtected && <span className="muted small">{env.kind === 'prod' ? 'production is protected' : 'this environment is protected'}</span>}
      </div>

      <section aria-label="Active pins">
        <h3>Active pins</h3>
        {err && (
          <LoadError>{err.message}</LoadError>
        )}
        {pinsQ.loading && !pinsQ.data && <SkeletonRows rows={2} />}
        {pinsQ.data && active.length === 0 && <p className="muted small">Nothing pinned: clients of this environment get no release.</p>}
        {active.length > 0 && (
          <ul className="pin-lines">
            {defaultPin ? pinLine(defaultPin) : <li className="muted small">No default pin.</li>}
            {ranges.map(pinLine)}
          </ul>
        )}
        <Button size="s" variant="ghost" icon={<History aria-hidden="true" />} aria-pressed={showHistory} onClick={() => setShowHistory((v) => !v)}>
          {showHistory ? 'Hide history' : 'Show history'}
        </Button>
        {showHistory && <PinHistory env={env.slug} releases={releases} />}
      </section>

      <Pointer env={env.slug} />
      </div>
      <div className="pane-foot">
        <Simulator env={env.slug} />
      </div>

      <PinDialog env={env} releases={releases} open={dialog === 'pin'} onClose={closeDialog} onDone={refresh} missingPermission={missing} initialReleaseId={initialReleaseId} />
      <RollbackDialog env={env} releases={releases} open={dialog === 'rollback'} onClose={closeDialog} onDone={refresh} missingPermission={missing} />
      {next && (
        <PromoteDialog
          source={env}
          target={next}
          releases={releases}
          open={dialog === 'promote'}
          onClose={closeDialog}
          onDone={() => {
            refresh();
            refreshEnv(next.slug);
          }}
          missingPermission={promoteMissing}
        />
      )}
    </article>
  );
}

function PinHistory({ env, releases }: { env: string; releases: Release[] }) {
  const { offsets } = useGame();
  const [zone] = useTimeZone();
  const q = usePinHistory(env);
  const when = (v: string | null | undefined) => formatInstant(v, zone, { offsets });
  if (q.error) return <LoadError>{asApiError(q.error).message}</LoadError>;
  if (!q.data) return <SkeletonRows rows={2} label="Loading the pin history" />;
  return (
    <ul className="pin-lines pin-history" data-testid={`pin-history-${env}`} aria-label="Pin history">
      {q.data.items.length === 0 && <li className="muted small">No pin yet.</li>}
      {q.data.items.map((pin) => {
        const state = pinState(pin);
        return (
          <li key={pin.id} className={`pin-line pin-${state}`} data-testid={`pin-history-${pin.id}`}>
            <span className="pin-what">
              <Badge tone={state === 'active' ? 'success' : state === 'revoked' ? 'danger' : 'neutral'}>{state}</Badge> {pin.isDefault ? <Badge tone="accent">default</Badge> : <Badge tone="info">{rangeText(pin)}</Badge>} <strong>{releaseName(pin.releaseId, releases)}</strong>
            </span>
            <span className="muted small">
              {pin.pinnedBy?.label ?? '—'} · {when(pin.createdAt)}
              {pin.revokedAt ? ` · revoked ${when(pin.revokedAt)}` : ''}
              {pin.reason ? ` · ${pin.reason}` : ''}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Pointer({ env }: { env: string }) {
  const { org, game } = useGame();
  const { client } = useAuth();
  const q = useQuery(keys.pointer(org, game.slug, env), () => client.data(ops.getPointer, { path: { org, game: game.slug, env } }));
  return (
    <details className="pointer" data-testid={`pointer-${env}`}>
      <summary>
        <ChevronRight className="icon-s pointer-chevron" aria-hidden="true" />
        Pointer
      </summary>
      <p className="muted small">What the clients read (`pointer.json`).</p>
      {q.error ? <p className="muted small">{asApiError(q.error).message}</p> : q.data === undefined ? <SkeletonRows rows={2} label="Loading the pointer" /> : <pre className="json">{pretty(q.data)}</pre>}
    </details>
  );
}

function Simulator({ env }: { env: string }) {
  const { org, game } = useGame();
  const { client } = useAuth();
  const [version, setVersion] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const resolve = async () => {
    const appVersion = version.trim();
    if (!appVersion) return;
    setBusy(true);
    setError(null);
    try {
      const r = (await client.data(ops.resolvePointer, { path: { org, game: game.slug, env }, query: { appVersion } })) as ResolvedRelease;
      const v = r.appVersion ?? appVersion;
      setResult(r.releaseId ? `App version ${v} gets release ${r.releaseId} (${r.matchedRange ? `range ${r.matchedRange}` : 'the default'})` : `App version ${v} gets no release (nothing is pinned for it)`);
    } catch (e) {
      setResult(null);
      setError(asApiError(e).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="simulator row"
      aria-label="Routing simulator"
      onSubmit={(e) => {
        e.preventDefault();
        void resolve();
      }}
    >
      <span className="small">Which release does an app version get?</span>
      <TextInput size="s" aria-label="App version" placeholder="1.4.0" value={version} onChange={(e) => setVersion(e.target.value)} autoComplete="off" />
      <Button size="s" type="submit" busy={busy} disabled={!version.trim()}>
        Resolve
      </Button>
      <span data-testid={`resolve-result-${env}`} aria-live="polite">
        {error ? <span className="error">{error}</span> : result ? <span className="resolve-result">{result}</span> : null}
      </span>
    </form>
  );
}
